import { useContext } from 'react';
import { ThemeContext, ThemeContextType } from '@/contexts';

/**
 * Custom hook for using the theme context with enhanced TypeScript support.
 * 
 * This hook provides easy access to theme functionality like:
 * - Switching between light/dark/system modes
 * - Selecting theme presets (default, solarized, nord, custom)
 * - Creating and applying custom theme overrides
 */
export const useTheme = (): ThemeContextType => {
    const context = useContext(ThemeContext);

    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }

    return context;
};

/**
 * Helper types for better IDE autocompletion and type safety
 */
export type UseThemeReturn = ReturnType<typeof useTheme>;

/**
 * Example usage:
 * 
 * ```tsx
 * function MyComponent() {
 *   const { 
 *     // Base theme controls
 *     currentTheme,      // 'dark' or 'light' - the actual applied theme
 *     theme,             // 'dark', 'light', or 'system' - the user's preference
 *     setTheme,          // Function to set theme
 *     toggleTheme,       // Function to cycle through themes
 *     
 *     // Theme preset controls
 *     themePreset,       // 'default', 'solarized', 'nord', or 'custom'
 *     setThemePreset,    // Function to set theme preset
 *     
 *     // Custom theming
 *     applyThemeColorOverrides,  // Function to override specific colors
 *   } = useTheme();
 *   
 *   // Example: Change to solarized theme
 *   const applySolarizedTheme = () => setThemePreset('solarized');
 *   
 *   // Example: Apply custom primary color
 *   const applyCustomPrimaryColor = () => {
 *     applyThemeColorOverrides({ 
 *       primary: 'oklch(0.6 0.24 120)',
 *       primaryForeground: 'oklch(0.98 0 0)'
 *     });
 *   };
 *   
 *   return (
 *     <div>
 *       Current theme: {currentTheme}
 *       <button onClick={() => setTheme('dark')}>Dark Mode</button>
 *       <button onClick={() => setTheme('light')}>Light Mode</button>
 *       <button onClick={() => setTheme('system')}>System Mode</button>
 *       <button onClick={toggleTheme}>Toggle Theme</button>
 *       
 *       <button onClick={applySolarizedTheme}>Solarized Theme</button>
 *       <button onClick={applyCustomPrimaryColor}>Custom Green</button>
 *     </div>
 *   );
 * }
 * ```
 */